import { useState, useMemo } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSitesFilters } from '../hooks/useSitesFilters';
import { useSites } from '../hooks/useSites';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { SitesListView } from '../components/v2/organisms/SitesListView';
import { EmptyStateSites } from '../components/v2/organisms/EmptyStateSites';
import { Routes } from '../../../routes';

type SiteStatusFilter = 'all' | 'active' | 'not-started';

const SITE_STATUS_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Not Started', value: 'not-started' },
] as const;

const SitesPage = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<SiteStatusFilter>('all');

  // Filter state management (includes debouncing)
  const { filters, searchValue, updateSearch } = useSitesFilters();

  // Data fetching with filters
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useSites(filters);

  const handleSiteClick = (siteId: number) => {
    navigate(`/sites/${siteId}`);
  };

  // Flatten all pages of sites
  const allSites = data?.pages.flatMap(page => page.items) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  // Client-side status filtering
  const sites = useMemo(() => {
    if (statusFilter === 'all') return allSites;
    return allSites.filter(site => {
      const progress = site.financialMetrics?.progress ?? 0;
      return statusFilter === 'active' ? progress > 0 : progress === 0;
    });
  }, [allSites, statusFilter]);

  const pageHeaderActions = (
    <PageHeaderAction
      label="New Site"
      onClick={() => navigate(Routes.sites.import)}
      icon={<AddIcon />}
      data-testid="new-site-button"
    />
  );

  const statusTabs = (
    <ChipTabs
      tabs={[...SITE_STATUS_TABS]}
      value={statusFilter}
      onChange={v => setStatusFilter(v as SiteStatusFilter)}
      data-testid="sites-status-tabs"
    />
  );

  // Loading state (initial load only - not during refetch)
  if (isLoading && !data) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error.message}
      </Alert>
    );
  }

  // Empty state
  if (allSites.length === 0 && !isLoading) {
    return (
      <Box>
        <PageHeader title="Sites" actions={pageHeaderActions} />
        <PageToolbar
          tabs={statusTabs}
          searchValue={searchValue}
          onSearchChange={updateSearch}
        />
        <EmptyStateSites />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box>
          <PageHeader title="Sites" actions={pageHeaderActions} />

          <PageToolbar
            tabs={statusTabs}
            searchValue={searchValue}
            onSearchChange={updateSearch}
          />

          <SitesListView
            sites={sites}
            loading={isLoading}
            onItemClick={handleSiteClick}
          />

          {hasNextPage && (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}
            >
              <Button
                variant="outlined"
                size="large"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage
                  ? 'Loading...'
                  : `Load More (${sites.length} of ${totalCount})`}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SitesPage;
